/**
 * AR Painting Helper - Realistic AR enhancement for wall-mounted artwork
 * Works within Model Viewer limitations but provides better UX for wall mounting
 */

class ARPaintingHelper {
  constructor() {
    this.init();
  }

  init() {
    document.addEventListener('DOMContentLoaded', () => {
      this.setupARHelpers();
    });
  }

  setupARHelpers() {
    const modelViewers = document.querySelectorAll('model-viewer');
    
    modelViewers.forEach(modelViewer => {
      this.enhanceARExperience(modelViewer);
    });
  }

  enhanceARExperience(modelViewer) {
    // Add realistic AR enhancements within Model Viewer capabilities
    this.configureOptimalSettings(modelViewer);
    this.addARInstructions(modelViewer);
    this.addVisualHints(modelViewer);
    
    // Listen for AR events
    modelViewer.addEventListener('ar-status', (event) => {
      this.handleAREvent(event, modelViewer);
    });
  }

  configureOptimalSettings(modelViewer) {
    // Configure Model Viewer for best painting display
    modelViewer.setAttribute('camera-controls', '');
    modelViewer.setAttribute('auto-rotate', 'false');
    modelViewer.setAttribute('shadow-intensity', '0.3');
    modelViewer.setAttribute('shadow-softness', '0.8');
    
    // Optimize for artwork viewing
    if (!modelViewer.hasAttribute('environment-image')) {
      modelViewer.setAttribute('environment-image', 'neutral');
    }
    
    // Better lighting for artwork
    modelViewer.setAttribute('exposure', '1.2');
  }

  addARInstructions(modelViewer) {
    // Create instruction overlay
    const instructionsContainer = this.createInstructionsOverlay();
    
    const container = modelViewer.closest('.product-media-modal__model') || modelViewer.parentElement;
    container.style.position = 'relative';
    container.appendChild(instructionsContainer);
    
    // Show instructions when AR button is visible
    this.showInstructionsOnHover(modelViewer, instructionsContainer);
  }

  createInstructionsOverlay() {
    const instructionsContainer = document.createElement('div');
    instructionsContainer.className = 'ar-painting-instructions';
    instructionsContainer.innerHTML = `
      <div class="ar-instructions-header">
        <span class="ar-instructions-icon">🖼️</span>
        <h4>Visualizza il quadro in AR</h4>
      </div>
      <div class="ar-instructions-content">
        <div class="ar-instruction-step">
          <span class="step-number">1</span>
          <span>Tocca il pulsante "Visualizza nel tuo spazio"</span>
        </div>
        <div class="ar-instruction-step">
          <span class="step-number">2</span>
          <span>Punta il telefono verso una parete</span>
        </div>
        <div class="ar-instruction-step">
          <span class="step-number">3</span>
          <span>Posiziona il quadro vicino alla parete</span>
        </div>
        <div class="ar-instruction-step">
          <span class="step-number">4</span>
          <span>Usa le dita per spostarlo e ridimensionarlo</span>
        </div>
      </div>
      <div class="ar-instructions-tip">
        <span class="tip-icon">💡</span>
        <span>Tip: Cammina intorno per vedere il quadro da diverse angolazioni</span>
      </div>
    `;
    
    instructionsContainer.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      background: linear-gradient(135deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.8) 100%);
      color: white;
      padding: 20px;
      border-radius: 12px;
      font-size: 13px;
      z-index: 100;
      transform: translateY(-100%);
      opacity: 0;
      transition: all 0.3s ease;
      pointer-events: none;
    `;
    
    return instructionsContainer;
  }

  showInstructionsOnHover(modelViewer, instructionsContainer) {
    const arButton = modelViewer.parentElement.querySelector('[data-shopify-xr]');
    
    if (arButton) {
      arButton.addEventListener('mouseenter', () => {
        instructionsContainer.style.transform = 'translateY(-10px)';
        instructionsContainer.style.opacity = '1';
      });
      
      arButton.addEventListener('mouseleave', () => {
        instructionsContainer.style.transform = 'translateY(-100%)';
        instructionsContainer.style.opacity = '0';
      });

      // Show on focus for mobile
      arButton.addEventListener('focus', () => {
        instructionsContainer.style.transform = 'translateY(-10px)';
        instructionsContainer.style.opacity = '1';
        
        // Hide after 3 seconds on mobile
        setTimeout(() => {
          instructionsContainer.style.transform = 'translateY(-100%)';
          instructionsContainer.style.opacity = '0';
        }, 3000);
      });
    }
  }

  addVisualHints(modelViewer) {
    // Add a visual indicator that this is optimized for wall viewing
    const hintBadge = document.createElement('div');
    hintBadge.className = 'ar-wall-optimized-badge';
    hintBadge.innerHTML = '📐 Ottimizzato per pareti';
    
    hintBadge.style.cssText = `
      position: absolute;
      top: 10px;
      right: 10px;
      background: rgba(0, 120, 255, 0.9);
      color: white;
      padding: 4px 8px;
      border-radius: 12px;
      font-size: 10px;
      font-weight: 500;
      z-index: 50;
      backdrop-filter: blur(4px);
    `;
    
    const container = modelViewer.closest('.product-media-modal__model') || modelViewer.parentElement;
    container.style.position = 'relative';
    container.appendChild(hintBadge);
  }

  handleAREvent(event, modelViewer) {
    const { status } = event.detail;
    
    switch (status) {
      case 'session-started':
        this.showARSessionTips(modelViewer);
        break;
      case 'object-placed':
        this.showPlacementSuccess(modelViewer);
        break;
      case 'session-ended':
        this.cleanupARSession(modelViewer);
        break;
    }
  }

  showARSessionTips(modelViewer) {
    // Show contextual tips during AR session
    const tipsOverlay = document.createElement('div');
    tipsOverlay.className = 'ar-session-tips';
    tipsOverlay.innerHTML = `
      <div class="ar-tip-content">
        <span class="ar-tip-icon">🎯</span>
        <span>Punta verso una parete per il miglior risultato</span>
      </div>
    `;
    
    tipsOverlay.style.cssText = `
      position: fixed;
      top: 80px;
      left: 50%;
      transform: translateX(-50%);
      background: rgba(0,0,0,0.8);
      color: white;
      padding: 12px 20px;
      border-radius: 20px;
      font-size: 14px;
      z-index: 2000;
      animation: fadeInOut 4s ease-in-out forwards;
    `;
    
    // Add fade animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes fadeInOut {
        0% { opacity: 0; transform: translateX(-50%) translateY(-10px); }
        20% { opacity: 1; transform: translateX(-50%) translateY(0); }
        80% { opacity: 1; transform: translateX(-50%) translateY(0); }
        100% { opacity: 0; transform: translateX(-50%) translateY(-10px); }
      }
    `;
    document.head.appendChild(style);
    
    document.body.appendChild(tipsOverlay);
    
    // Remove after animation
    setTimeout(() => {
      if (tipsOverlay.parentNode) {
        tipsOverlay.parentNode.removeChild(tipsOverlay);
      }
      if (style.parentNode) {
        style.parentNode.removeChild(style);
      }
    }, 4000);
  }

  showPlacementSuccess(modelViewer) {
    // Show success message when object is placed
    const successTip = document.createElement('div');
    successTip.className = 'ar-placement-success';
    successTip.innerHTML = `
      <span class="success-icon">✨</span>
      <span>Perfetto! Ora puoi spostarlo e ridimensionarlo</span>
    `;
    
    successTip.style.cssText = `
      position: fixed;
      bottom: 100px;
      left: 50%;
      transform: translateX(-50%);
      background: rgba(34, 197, 94, 0.9);
      color: white;
      padding: 12px 20px;
      border-radius: 20px;
      font-size: 14px;
      z-index: 2000;
      animation: fadeInOut 3s ease-in-out forwards;
    `;
    
    document.body.appendChild(successTip);
    
    setTimeout(() => {
      if (successTip.parentNode) {
        successTip.parentNode.removeChild(successTip);
      }
    }, 3000);
  }

  cleanupARSession(modelViewer) {
    // Clean up any AR-related overlays
    const arOverlays = document.querySelectorAll('.ar-session-tips, .ar-placement-success');
    arOverlays.forEach(overlay => {
      if (overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
    });
  }
}

// Initialize AR Painting Helper
new ARPaintingHelper();

// Make it available globally
window.ARPaintingHelper = ARPaintingHelper;